import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { subscribeApi } from '../services/api';
import { CheckCircle2, XCircle, Loader2, MailX } from 'lucide-react';

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function handleUnsubscribe() {
    if (!token) {
      setState('error');
      setMessage('This unsubscribe link is missing its token.');
      return;
    }
    setState('loading');
    try {
      const res = await subscribeApi.unsubscribe(token);
      setState('done');
      setMessage(res.message);
    } catch (err: unknown) {
      setState('error');
      setMessage(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  return (
    <div className="py-16">
      <div className="container-max max-w-md mx-auto">
        <h1 className="text-3xl font-serif font-bold text-coastal-600 mb-2 text-center">Unsubscribe</h1>

        <Card>
          <CardContent className="pt-6">
            {state === 'loading' && (
              <div className="text-center space-y-3 py-4">
                <Loader2 className="w-10 h-10 text-coastal-600 animate-spin mx-auto" />
                <p className="text-gray-600">Just a moment…</p>
              </div>
            )}

            {state === 'done' && (
              <div className="text-center space-y-4">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                <p className="text-gray-700">{message}</p>
                <Link to="/" className="text-coastal-600 font-medium hover:underline inline-block">
                  Back to home
                </Link>
              </div>
            )}

            {state === 'error' && (
              <div className="text-center space-y-4">
                <XCircle className="w-12 h-12 text-red-500 mx-auto" />
                <p className="text-red-600">{message}</p>
                <Link to="/locations" className="text-coastal-600 font-medium hover:underline inline-block">
                  Back to Where to Find Us
                </Link>
              </div>
            )}

            {state === 'idle' && (
              <div className="text-center space-y-4">
                <MailX className="w-12 h-12 text-gray-400 mx-auto" />
                <p className="text-gray-700">
                  Are you sure you want to stop receiving Coastal Harvest Co updates?
                </p>
                <Button variant="outline" onClick={handleUnsubscribe} disabled={!token}>
                  Yes, unsubscribe me
                </Button>
                <div>
                  <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
                    No, keep me subscribed
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
